import { Injectable } from '@angular/core';
import { CreateAssetDto } from '@thehubz/client/data-access-api';
import { AnyType } from '@thehubz/shared/model';
import * as Konva from 'konva';
import { Stage } from 'konva/lib/Stage';
import { Vector2d } from 'konva/lib/types';

@Injectable({
  providedIn: 'root',
})
export class AssetAnnotatorService {
  annotations: AnyType[] = [];
  stageJSONData: any = null;
  konvaStage!: Stage;
  konvaLayer: any = null;
  originalWidth = 0;
  originalHeight = 0;
  color = '#FFFFFF';
  strokeWidth = 1;
  drawLine = false;
  straightLine = false;
  isDrawing = false;
  lastLine: any = null;
  assetType = '';
  videoTimestamp = 0;
  pdfPage = 0;
  assetTypes = CreateAssetDto.AssetTypeEnum;
  supportedAssetTypes = [this.assetTypes.Image, this.assetTypes.Video, this.assetTypes.Pdf];

  initKonvaStage(canvasID: string, canvasWidth: number, canvasHeight: number, assetType: string, pageNum?: number, timestamp?: number) {
    this.assetType = assetType;
    this.pdfPage = pageNum || 0;
    this.videoTimestamp = timestamp || 0;
    this.originalWidth = canvasWidth;
    this.originalHeight = canvasHeight;
    let initialPositionY = 0;
    if (this.stageJSONData) {
      this.filterAnnotationsToDisplay();
      this.konvaStage = Konva.default.Node.create(JSON.stringify(this.stageJSONData), canvasID);
      this.konvaLayer = this.konvaStage.find('Layer')[0];
      // if saved canvas dimension are different than current view than resize according to current view
      if (this.konvaStage.width() != canvasWidth || this.konvaStage.height() != canvasHeight) {
        this.originalWidth = this.konvaStage.width();
        this.originalHeight = this.konvaStage.height();
        this.resizeKovnvaCanvas(canvasWidth, canvasHeight);
      }
    } else {
      this.konvaStage = new Konva.default.Stage({
        height: canvasHeight,
        width: canvasWidth,
        container: canvasID,
        draggable: false,
      });

      this.konvaLayer = new Konva.default.Layer();
      this.konvaStage.add(this.konvaLayer);
    }

    this.konvaStage.on('mousedown touchstart', () => {
      if (this.drawLine) {
        this.isDrawing = true;
        this.removeUnsavedAnnotations();
        const pos = this.getRelativePointerPosition(this.konvaStage);
        initialPositionY = 0;
        if (this.straightLine) {
          initialPositionY = pos.y;
        }
        this.lastLine = new Konva.default.Line({
          stroke: this.color,
          strokeWidth: this.strokeWidth,
          globalCompositeOperation: 'source-over', // 'destination-out',
          // round cap for smoother lines
          lineCap: 'round',
          lineJoin: 'round',
          bezier: true,
          // add point twice, so we have some drawings even on a simple click
          points: [pos.x, pos.y, pos.x, pos.y],
        });
        this.lastLine.setAttr('isSaved', false);
        if (this.assetType == this.assetTypes.Video) {
          this.lastLine.setAttr('timestamp', this.videoTimestamp);
        } else if (this.assetType == this.assetTypes.Pdf) {
          this.lastLine.setAttr('page', this.pdfPage);
        }

        this.konvaLayer.add(this.lastLine);
      }
    });

    this.konvaStage.on('mouseup touchend', () => {
      this.isDrawing = false;
    });

    // and core function - drawing
    this.konvaStage.on('mousemove touchmove', (e) => {
      if (!this.isDrawing) {
        return;
      }

      // prevent scrolling on touch devices
      e.evt.preventDefault();

      const pos = this.getRelativePointerPosition(this.konvaStage);
      const newPoints = this.lastLine.points().concat([pos?.x, initialPositionY ? initialPositionY : pos?.y]);
      this.lastLine.points(newPoints);
    });
  }

  /**
   * Get relative pointer position even after zooming
   * @param node
   * @returns
   */
  getRelativePointerPosition(node: Stage) {
    // the function will return pointer position relative to the passed node
    const transform = node.getAbsoluteTransform().copy();
    // to detect relative position we need to invert transform
    transform.invert();
    // get pointer (say mouse or touch) position
    const pos = node.getPointerPosition() || ({} as Vector2d);
    // now we find relative point
    return transform.point(pos);
  }

  filterAnnotationsToDisplay() {
    //filtering the annotations
    if (this.assetType == this.assetTypes.Video) {
      this.annotations.sort((a, b) => {
        return a.attrs.timestamp - b.attrs.timestamp;
      });
      this.stageJSONData.children[0].children = this.annotations.filter((a) => a.attrs.timestamp === this.videoTimestamp);
    } else if (this.assetType == this.assetTypes.Pdf) {
      this.annotations.sort((a, b) => {
        return a.attrs.page - b.attrs.page;
      });
      this.stageJSONData.children[0].children = this.annotations.filter((a) => a.attrs.page === this.pdfPage);
    }
  }

  drawLayer() {
    this.konvaLayer.draw();
  }

  destroyStage() {
    this.konvaStage.destroy();
  }

  drawTool(tool: string, assetType: string, scrollLeft?: number, scrollTop?: number) {
    //add new annotation
    let scrollPosition = {
      scrollLeft: scrollLeft,
      scrollTop: scrollTop,
    };
    if (scrollLeft || scrollTop) {
      scrollPosition = this.calculateScrollPosition(scrollLeft || 0, scrollTop || 0);
    }
    let shape;
    this.straightLine = false;
    if (tool == 'rectangle') {
      shape = new Konva.default.Rect({
        x: 50 + (scrollPosition.scrollLeft ? scrollPosition.scrollLeft : 0),
        y: 50 + (scrollPosition.scrollTop ? scrollPosition.scrollTop : 0),
        width: 100,
        height: 100,
        stroke: this.color,
        strokeWidth: this.strokeWidth,
        draggable: true,
      });
      this.drawLine = false;
    } else if (tool == 'circle') {
      shape = new Konva.default.Circle({
        x: 100 + (scrollPosition.scrollLeft ? scrollPosition.scrollLeft : 0),
        y: 100 + (scrollPosition.scrollTop ? scrollPosition.scrollTop : 0),
        width: 100,
        height: 100,
        stroke: this.color,
        strokeWidth: this.strokeWidth,
        draggable: true,
      });
      this.drawLine = false;
    } else if (tool == 'pen') {
      this.drawLine = true;
    } else if (tool == 'marker') {
      this.drawLine = true;
      this.straightLine = true;
    }

    if (shape) {
      shape.setAttr('isSaved', false);
      if (assetType == this.assetTypes.Video) {
        shape.setAttr('timestamp', this.videoTimestamp);
      } else if (assetType == this.assetTypes.Pdf) {
        shape.setAttr('page', this.pdfPage);
      }

      this.konvaLayer.add(shape);

      const transformer = new Konva.default.Transformer({
        nodes: [shape],
        rotateAnchorOffset: 50,
        // enabledAnchors: ['top-left', 'top-right', 'bottom-left', 'bottom-right'],
      });

      transformer.setAttr('isSaved', false);
      if (assetType == this.assetTypes.Video) {
        transformer.setAttr('timestamp', this.videoTimestamp);
      } else if (assetType == this.assetTypes.Pdf) {
        transformer.setAttr('page', this.pdfPage);
      }

      this.konvaLayer.add(transformer);
      this.konvaLayer.draw();
    }
  }

  calculateScrollPosition(scrollLeft: number, scrollTop: number) {
    const canvasWidth = this.originalWidth;
    const canvasHeight = this.originalHeight;
    let scrollLeftPosition = scrollLeft;
    let scrollTopPosition = scrollTop;
    if (this.konvaStage.width() != canvasWidth || this.konvaStage.height() != canvasHeight) {
      const scaleX = this.konvaStage.width() / canvasWidth;
      const scaleY = this.konvaStage.height() / canvasHeight;
      scrollLeftPosition = scrollLeft / scaleX;
      scrollTopPosition = scrollTop / scaleY;
    }
    return { scrollLeft: scrollLeftPosition, scrollTop: scrollTopPosition };
  }

  removeUnsavedAnnotations() {
    if (this.konvaLayer.children.length > 0) {
      for (let i = 0; i < this.konvaLayer.children.length; i++) {
        const node = this.konvaLayer.children[i];
        if (!node.getAttr('isSaved') && typeof node.destroy === 'function') {
          node.destroy();
          i--;
        }
      }
      this.konvaLayer.draw();
    }
  }

  changeCurrentNodeColor(value: string) {
    if (this.konvaLayer.children.length > 0) {
      for (const node of this.konvaLayer.children) {
        if (!node.getAttr('isSaved') && typeof node.stroke === 'function') {
          node.stroke(value);
        }
      }
    }
  }

  changeCurrentNodeStroke(value: number) {
    if (this.konvaLayer.children.length > 0) {
      for (const node of this.konvaLayer.children) {
        if (!node.getAttr('isSaved') && typeof node.strokeWidth === 'function') {
          node.strokeWidth(value);
        }
      }
    }
  }

  toggleAnnotationVisibility(annotation: AnyType): boolean {
    const annotationNodes = this.findKonvaNodeFromAnnotation(annotation);
    if (annotationNodes[0].isVisible()) annotationNodes[0].hide();
    else annotationNodes[0].show();

    return annotationNodes[0].isVisible();
  }

  makeAnnotationEditable(annotation: AnyType) {
    const annotationNodes = this.findKonvaNodeFromAnnotation(annotation);
    const konvaAnnotation = annotationNodes[0];
    konvaAnnotation.setAttr('draggable', true);
    if (annotationNodes[1]) {
      //attaching the transformer node to shape
      annotationNodes[1].attachTo(konvaAnnotation);
    }
    return konvaAnnotation;
  }

  addAnnotationComment(newComment: string, author: any, updatedAt: string, noAnnotatedFlag: boolean): { annotation: AnyType; transformer?: AnyType } {
    let newAnnotation = null;
    if (!noAnnotatedFlag) {
      // While saving the comments without annotation
      const lastLine = new Konva.default.Line({
        stroke: this.color,
        strokeWidth: this.strokeWidth,
        globalCompositeOperation: 'source-over', // 'destination-out',
        lineCap: 'round',
        lineJoin: 'round',
        bezier: true,
        points: [0, 0, 0, 0],
      });
      this.konvaLayer.add(lastLine);
      newAnnotation = this.konvaLayer.children[this.konvaLayer.children.length - 1];
    } else {
      newAnnotation = this.konvaLayer.children[this.konvaLayer.children.length - 1];
    }
    let transformer;
    if (newAnnotation && newAnnotation.getType() == 'Group') {
      newAnnotation.setAttr('isSaved', true); //marking data as saved before submitting
      transformer = this.konvaLayer.children[this.konvaLayer.children.length - 1].toObject();
      newAnnotation = this.konvaLayer.children[this.konvaLayer.children.length - 2];
    }
    newAnnotation.setAttr('isSaved', true);
    newAnnotation.setAttr('draggable', false);
    newAnnotation.setAttr('comment', newComment);
    newAnnotation.setAttr('createdBy', author);
    newAnnotation.setAttr('updatedAt', updatedAt);
    return { annotation: newAnnotation.toObject(), transformer: transformer };
  }

  updateAnnotationComment(konvaAnnotation: AnyType, newComment: string, updatedAt: string) {
    konvaAnnotation.setAttr('comment', newComment);
    konvaAnnotation.setAttr('updatedAt', updatedAt);
    konvaAnnotation.setAttr('draggable', false);
  }

  deleteAnnotation(annotation: AnyType) {
    const annotationNodes = this.findKonvaNodeFromAnnotation(annotation);
    const konvaAnnotation = annotationNodes[0];
    konvaAnnotation.destroy();
    if (annotationNodes[1]) {
      //destroying the transformer node
      annotationNodes[1].destroy();
    }
    this.konvaLayer.draw();
  }

  findKonvaNodeFromAnnotation(annotation: AnyType): AnyType[] {
    const nodes = [];
    const shapes = this.konvaLayer.children;
    for (let i = 0; i < shapes.length; i++) {
      if (shapes[i].getAttr('comment') == annotation.attrs.comment && shapes[i].getAttr('updatedAt') == annotation.attrs.updatedAt) {
        nodes.push(shapes[i]);
        if (shapes[i].className !== 'Line') {
          const transformer = shapes[i + 1];
          nodes.push(transformer);
        }
        break;
      }
    }
    return nodes;
  }

  resizeKovnvaCanvas(newWidth: number, newHeight: number, zooming = false) {
    //below code sets the dimensions and scale of the canvas
    let scaleX = newWidth / this.konvaStage.width();
    let scaleY = newHeight / this.konvaStage.height();

    //below code finds the true scale when zoom in/out
    if (zooming) {
      const oldWidth = this.konvaStage.width() / this.konvaStage.scaleX();
      const oldHeight = this.konvaStage.height() / this.konvaStage.scaleY();
      scaleX = newWidth / oldWidth;
      scaleY = newHeight / oldHeight;
    }

    this.konvaStage.width(newWidth);
    this.konvaStage.height(newHeight);

    this.konvaStage.scale({ x: scaleX, y: scaleY });
  }

  resetCanvasToOriginalSize() {
    if (this.originalWidth || this.originalHeight) {
      this.stageJSONData.attrs.width = this.originalWidth;
      this.stageJSONData.attrs.height = this.originalHeight;
      this.stageJSONData.attrs.scaleX = 1;
      this.stageJSONData.attrs.scaleY = 1;
    }
  }

  detachAllTransformers() {
    const nodes = this.konvaLayer.children;
    if (nodes && nodes.length > 0) {
      for (const node of nodes) {
        if (node.className == 'Transformer') node.detach();
      }
    }
  }

  isSupportedForAnnotation(assetType: any) {
    return this.supportedAssetTypes.indexOf(assetType) != -1;
  }
}
